import { ClaimReferralResponse } from '../services/referralsService'
import { PENDING_REFERRAL_ATTRIBUTION_KEY } from './static-constants'
import { errorLog } from '../utils/log-helper'

/**
 * Attribution data persisted after claiming a referral code
 */
export interface ReferralAttribution {
  referralCode: string
  referralId?: string
  context?: string
  referrerEaterId?: string | null
  referrerRestaurantId?: string | null
  claimedAt: number
}

/**
 * TTL for attribution data (14 days in milliseconds)
 */
const ATTRIBUTION_TTL_MS = 14 * 24 * 60 * 60 * 1000

/**
 * Persist referral attribution after successful claim
 */
export function persistReferralAttribution(
  claimResponse: ClaimReferralResponse,
  referralCode: string,
): void {
  // Only persist if we have at least one referrer ID
  if (!claimResponse.referrerEaterId && !claimResponse.referrerRestaurantId) {
    return
  }

  const attribution: ReferralAttribution = {
    referralCode,
    referralId: claimResponse.referralId,
    context: claimResponse.context,
    referrerEaterId: claimResponse.referrerEaterId ?? null,
    referrerRestaurantId: claimResponse.referrerRestaurantId ?? null,
    claimedAt: Date.now(),
  }

  try {
    localStorage.setItem(PENDING_REFERRAL_ATTRIBUTION_KEY, JSON.stringify(attribution))
  } catch (error) {
    // localStorage may be full or unavailable - fail silently
    if (process.env.NODE_ENV !== 'production') {
      errorLog('Failed to persist referral attribution', error)
    }
  }
}

/**
 * Get persisted referral attribution if valid and not expired
 */
export function getReferralAttribution(): ReferralAttribution | null {
  try {
    const stored = localStorage.getItem(PENDING_REFERRAL_ATTRIBUTION_KEY)
    if (!stored) {
      return null
    }

    const attribution: ReferralAttribution = JSON.parse(stored)

    // Check if expired
    const age = Date.now() - attribution.claimedAt
    if (age > ATTRIBUTION_TTL_MS) {
      // Expired - clean up
      clearReferralAttribution()
      return null
    }

    return attribution
  } catch (error) {
    // Invalid JSON or other error - clean up
    clearReferralAttribution()
    return null
  }
}

/**
 * Clear persisted referral attribution
 */
export function clearReferralAttribution(): void {
  try {
    localStorage.removeItem(PENDING_REFERRAL_ATTRIBUTION_KEY)
  } catch (error) {
    // Ignore errors
  }
}

